import type { IMonthlyClientFinanceSummary } from '../../models/IMonthlyClientFinance';
import { useTheme } from '@mui/material/styles';
import './monthlyClientFinance.css';

type Props = { data?: IMonthlyClientFinanceSummary; isLoading: boolean };

const money = (value?: number | null) => (value == null ? '—' : `${value.toFixed(2)} EUR`);
const hours = (value: number) => value.toFixed(2);
const percent = (value?: number | null) => (value == null ? '—' : `${value.toFixed(2)}%`);
const periodLabel = (start: string, end: string) => {
  const first = new Date(start);
  const last = new Date(new Date(end).getTime() - 24 * 60 * 60 * 1000);
  return `${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${last.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};
const metricClass = (metric: 'com' | 'gp' | 'gpm', value?: number | null) => {
  if (value == null) return '';
  if (metric === 'com') return value > 90 ? 'positive' : value >= 75 ? 'warning' : 'negative';
  if (metric === 'gp') return value > 0 ? 'positive' : value < 0 ? 'negative' : '';
  return value > 50 ? 'positive' : value >= 30 ? 'warning' : 'negative';
};
const mom = (value: number | null, percentagePoints: boolean) => {
  if (value == null) return 'MoM: —';
  if (value === 0) return `MoM: — 0.00${percentagePoints ? ' pp' : '%'}`;
  return `MoM: ${value > 0 ? '↑' : '↓'} ${Math.abs(value).toFixed(2)}${percentagePoints ? ' pp' : '%'}`;
};

export const MonthlyClientFinanceDashboard = ({ data, isLoading }: Props) => {
  const theme = useTheme();

  if (isLoading) return <div className="monthly-finance-loading">Loading finance summary…</div>;
  if (!data) return null;

  const kpis: Array<[string, number, number | null, 'money' | 'percent', boolean?]> = [
    ['Sales', data.CurrentMonth.Sales, data.MonthOverMonth.SalesPercent, 'money'],
    ['COM%', data.CurrentMonth.ComPercent, data.MonthOverMonth.ComPercentagePoints, 'percent'],
    ['Cost', data.CurrentMonth.Cost, data.MonthOverMonth.CostPercent, 'money', true],
    ['GP', data.CurrentMonth.GrossProfit, data.MonthOverMonth.GrossProfitPercent, 'money'],
    ['GPm%', data.CurrentMonth.GpmPercent, data.MonthOverMonth.GpmPercentagePoints, 'percent'],
  ];

  return (
    <div className={`monthly-finance monthly-finance--${theme.palette.mode}`}>
      <div className="monthly-finance-kpis">
        {kpis.map(([label, value, change, format, inverse]) => (
          <div className="monthly-finance-kpi" key={label}>
            <span>{label}</span>
            <strong>{format === 'money' ? money(value) : percent(value)}</strong>
            <small
              className={
                change == null || change === 0
                  ? ''
                  : change > 0 !== Boolean(inverse)
                    ? 'positive'
                    : 'negative'
              }
            >
              {mom(change, format === 'percent')}
            </small>
          </div>
        ))}
      </div>
      {data.Warnings.map((warning) => (
        <div className="monthly-finance-warning" key={warning}>
          {warning}
        </div>
      ))}
      <div className="monthly-finance-table-wrap">
        <table className="monthly-finance-table">
          <thead>
            <tr>
              <th>Contract / Employee</th>
              {data.Periods.map((period) => (
                <th key={period.Start}>{periodLabel(period.Start, period.End)}</th>
              ))}
              <th title="Hours paid by the Client.">Billable hours</th>
              <th title="All tracked hours in the selected Client scope.">Total hours</th>
              <th title="Billable hours divided by total hours.">COM%</th>
              <th>Employee Rate for OSKI</th>
              <th>Employee Rate for Client</th>
              <th title="Billable hours × effective Client rate.">Sales</th>
              <th title="OSKI rate × total hours.">Cost</th>
              <th title="Sales minus Cost.">GP</th>
              <th title="GP divided by Sales.">GPm%</th>
            </tr>
          </thead>
          <tbody>
            {data.Employees.map((row) => (
              <tr key={row.EmployeeId}>
                <td>
                  <div>{row.EmployeeName}</div>
                  <small>{row.ContractType || '—'}</small>
                </td>
                {row.WeeklyHours.map((value, index) => (
                  <td key={index}>{hours(value)}</td>
                ))}
                <td>{hours(row.BillableHours)}</td>
                <td>{hours(row.TotalHours)}</td>
                <td className={metricClass('com', row.ComPercent)}>{percent(row.ComPercent)}</td>
                <td>{row.EmployeeRateForOskiStatus || money(row.EmployeeRateForOski)}</td>
                <td>{money(row.EmployeeRateForClient)}</td>
                <td>{money(row.Sales)}</td>
                <td>{money(row.Cost)}</td>
                <td className={metricClass('gp', row.GrossProfit)}>{money(row.GrossProfit)}</td>
                <td className={metricClass('gpm', row.GpmPercent)}>{percent(row.GpmPercent)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              {data.Total.WeeklyHours.map((value, index) => (
                <th key={index}>{hours(value)}</th>
              ))}
              <th>{hours(data.Total.BillableHours)}</th>
              <th>{hours(data.Total.TotalHours)}</th>
              <th className={metricClass('com', data.Total.ComPercent)}>
                {percent(data.Total.ComPercent)}
              </th>
              <th>—</th>
              <th>—</th>
              <th>{money(data.Total.Sales)}</th>
              <th>{money(data.Total.Cost)}</th>
              <th className={metricClass('gp', data.Total.GrossProfit)}>
                {money(data.Total.GrossProfit)}
              </th>
              <th className={metricClass('gpm', data.Total.GpmPercent)}>
                {percent(data.Total.GpmPercent)}
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
